import { Ban, Check, FileText, Printer, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../api';
import ComprobanteEgreso from './ComprobanteEgreso';
import { useComprobanteEgreso } from '../hooks/useComprobanteEgreso';
import { useImpresionCheque } from '../hooks/useImpresionCheque';
import ModalMotivo from './ModalMotivo';
import VistaPreviaImpresion from './VistaPreviaImpresion';

export default function Aprobaciones() {
  const [pendientes, setPendientes] = useState([]);
  const [aprobados, setAprobados] = useState([]);
  const [reimpresiones, setReimpresiones] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [modalMotivo, setModalMotivo] = useState(null); // { tipo: 'rechazar' | 'anular' | 'rechazar-reimpresion', id }

  const { preparando, prepararImpresion, confirmarImpresion, cancelar } = useImpresionCheque(cargar);
  const {
    preparando: preparandoComprobante,
    prepararComprobante,
    imprimir: imprimirComprobante,
    cancelar: cancelarComprobante,
  } = useComprobanteEgreso();

  function cargar() {
    setCargando(true);
    Promise.all([
      api.get('/cheques/', { params: { estado: 'pendiente' } }),
      api.get('/cheques/', { params: { estado: 'aprobado' } }),
      api.get('/reimpresiones/'),
    ])
      .then(([pendientesRes, aprobadosRes, reimpresionesRes]) => {
        setPendientes(pendientesRes.data.results);
        setAprobados(aprobadosRes.data.results);
        setReimpresiones(reimpresionesRes.data);
      })
      .finally(() => setCargando(false));
  }

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function aprobar(id) {
    try {
      await api.post(`/cheques/${id}/aprobar/`);
      toast.success('Cheque aprobado.');
      cargar();
    } catch {
      toast.error('No se pudo aprobar.');
    }
  }

  async function aprobarReimpresion(id) {
    try {
      await api.post(`/reimpresiones/${id}/aprobar/`);
      toast.success('Reimpresión aprobada.');
      cargar();
    } catch {
      toast.error('No se pudo aprobar la reimpresión.');
    }
  }

  async function confirmarModalMotivo(motivo) {
    const { tipo, id } = modalMotivo;
    try {
      if (tipo === 'rechazar') {
        await api.post(`/cheques/${id}/rechazar/`, { motivo });
        toast.success('Cheque rechazado.');
      } else if (tipo === 'anular') {
        await api.post(`/cheques/${id}/anular/`, { motivo });
        toast.success('Cheque anulado.');
      } else {
        await api.post(`/reimpresiones/${id}/rechazar/`, { motivo });
        toast.success('Reimpresión rechazada.');
      }
      setModalMotivo(null);
      cargar();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo completar la acción.');
    }
  }

  if (cargando) return <p className="no-imprimir">Cargando…</p>;

  return (
    <div className="aprobaciones">
      <div className="control-group no-imprimir">
        <h4>Pendientes de aprobar</h4>
        {pendientes.length === 0 ? (
          <p>No hay solicitudes pendientes.</p>
        ) : (
          <table className="tabla-historial">
            <thead>
              <tr>
                <th>Fecha</th><th>Banco</th><th>N.º cheque</th><th>Beneficiario</th><th>Concepto</th>
                <th className="celda-monto">Monto</th><th>Solicitado por</th><th></th>
              </tr>
            </thead>
            <tbody>
              {pendientes.map((c) => (
                <tr key={c.id}>
                  <td>{c.fecha_emision}</td>
                  <td>{c.banco_nombre}</td>
                  <td>{c.numero_cheque || '—'}</td>
                  <td>{c.beneficiario}</td>
                  <td className="celda-concepto">{c.concepto}</td>
                  <td className="celda-monto">${Number(c.monto_numero).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                  <td title={c.creado_por_nombre}>{c.creado_por_iniciales}</td>
                  <td>
                    <div className="acciones-tabla">
                      <button type="button" className="btn-secundario btn-aprobar" onClick={() => aprobar(c.id)}>
                        <Check size={14} /> Aprobar
                      </button>
                      <button type="button" className="btn-secundario btn-rechazar" onClick={() => setModalMotivo({ tipo: 'rechazar', id: c.id })}>
                        <X size={14} /> Rechazar
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {reimpresiones.length > 0 && (
        <div className="control-group no-imprimir">
          <h4>Solicitudes de reimpresión</h4>
          <table className="tabla-historial">
            <thead>
              <tr>
                <th>Fecha</th><th>N.º cheque</th><th>Beneficiario</th><th>Motivo</th><th>Solicitado por</th><th></th>
              </tr>
            </thead>
            <tbody>
              {reimpresiones.map((r) => (
                <tr key={r.id}>
                  <td>{r.cheque_fecha_emision}</td>
                  <td>{r.cheque_numero || '—'}</td>
                  <td>{r.cheque_beneficiario}</td>
                  <td className="celda-concepto">{r.motivo}</td>
                  <td>{r.solicitado_por_nombre}</td>
                  <td>
                    <div className="acciones-tabla">
                      <button type="button" className="btn-secundario btn-aprobar" onClick={() => aprobarReimpresion(r.id)}>
                        <Check size={14} /> Aprobar
                      </button>
                      <button type="button" className="btn-secundario btn-rechazar" onClick={() => setModalMotivo({ tipo: 'rechazar-reimpresion', id: r.id })}>
                        <X size={14} /> Rechazar
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="control-group no-imprimir">
        <h4>Aprobados, listos para imprimir</h4>
        {aprobados.length === 0 ? (
          <p>No hay cheques aprobados esperando impresión.</p>
        ) : (
          <table className="tabla-historial">
            <thead>
              <tr>
                <th>Fecha</th><th>Banco</th><th>N.º cheque</th><th>Beneficiario</th><th>Concepto</th>
                <th className="celda-monto">Monto</th><th>Aprobado por</th><th>Imprimir</th>
              </tr>
            </thead>
            <tbody>
              {aprobados.map((c) => (
                <tr key={c.id}>
                  <td>{c.fecha_emision}</td>
                  <td>{c.banco_nombre}</td>
                  <td>{c.numero_cheque || '—'}</td>
                  <td>{c.beneficiario}</td>
                  <td className="celda-concepto">{c.concepto}</td>
                  <td className="celda-monto">${Number(c.monto_numero).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                  <td title={c.aprobado_por_nombre}>{c.aprobado_por_iniciales}</td>
                  <td>
                    <div className="acciones-tabla">
                      <button type="button" className="btn-secundario btn-imprimir" onClick={() => prepararComprobante(c)}>
                        <FileText size={14} /> Comprobante
                      </button>
                      <button type="button" className="btn-secundario btn-imprimir" onClick={() => prepararImpresion(c)}>
                        <Printer size={14} /> Cheque
                      </button>
                      <button type="button" className="btn-secundario btn-peligro" onClick={() => setModalMotivo({ tipo: 'anular', id: c.id })}>
                        <Ban size={14} /> Anular
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <VistaPreviaImpresion preparando={preparando} onCancelar={cancelar} onConfirmar={confirmarImpresion} />
      <ComprobanteEgreso preparando={preparandoComprobante} onCancelar={cancelarComprobante} onImprimir={imprimirComprobante} />
      {modalMotivo && (
        <ModalMotivo
          titulo={
            modalMotivo.tipo === 'rechazar' ? 'Rechazar cheque'
              : modalMotivo.tipo === 'anular' ? 'Anular cheque'
              : 'Rechazar reimpresión'
          }
          ayuda={
            modalMotivo.tipo === 'rechazar' ? '¿Por qué se rechaza esta solicitud? (opcional)'
              : modalMotivo.tipo === 'anular' ? '¿Por qué se anula este cheque? Esta acción no se puede deshacer.'
              : '¿Por qué se rechaza esta reimpresión?'
          }
          requerido={modalMotivo.tipo === 'anular'}
          textoConfirmar={modalMotivo.tipo === 'anular' ? 'Anular' : 'Rechazar'}
          onCancelar={() => setModalMotivo(null)}
          onConfirmar={confirmarModalMotivo}
        />
      )}
    </div>
  );
}
